<template>
  <div class="pa-4">
    <breadcrumb />

    <div class="flex-wrap mb-4 d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <h2 class="mr-3 black--text">{{ $t("reports.farmProductivity.title") }}</h2>
        <v-btn small rounded color="primary" depressed @click="showGuide = true">
          <v-icon left small>mdi-help-circle-outline</v-icon>
          {{ $t("reports.farmProductivity.aiReportGuide") }}
        </v-btn>
      </div>
      <div>
        <v-btn outlined color="primary" class="mr-2" @click="goBack">{{ $t("reports.farmProductivity.back") }}</v-btn>
        <v-btn outlined color="red" class="mr-2" @click="deleteDialog = true">{{ $t("reports.farmProductivity.deleteReport") }}</v-btn>
        <v-btn color="primary" depressed :disabled="!isDownloadable" :loading="downloading" @click="download">
          {{ $t("reports.farmProductivity.downloadReport") }}
        </v-btn>
      </div>
    </div>

    <v-card class="pa-4" outlined v-if="report">
      <!-- Meta -->
      <h3 class="mb-3 black--text">{{ report.farmName ? report.farmName + " Report" : $t("reports.farmProductivity.title") }}</h3>
      <v-row class="mb-1">
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.farmProductivity.dateOfReport") }}</div>
          <div class="font-weight-bold">{{ report.createdAt | formatDate }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.farmProductivity.farm") }}</div>
          <div class="font-weight-bold">{{ report.farmName || "-" }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.farmProductivity.zone") }}</div>
          <div class="font-weight-bold">{{ report.zoneName || "-" }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.farmProductivity.location") }}</div>
          <div class="font-weight-bold">{{ report.locationName || "-" }}</div>
          <div v-if="report.centerLatitude != null" class="grey--text text-caption">
            {{ coordLabel }}
          </div>
        </v-col>
      </v-row>

      <!-- Map + status overlay -->
      <div class="mt-2 map-holder">
        <div v-if="isGenerating" class="gen-overlay">
          <v-progress-circular indeterminate color="primary" size="42" />
          <div class="mt-3 black--text">{{ $t("reports.farmProductivity.generating") }}</div>
          <div class="grey--text text-caption">{{ $t("reports.farmProductivity.generatingHelp") }}</div>
        </div>
        <index-overlay-map
          :overlays="overlays"
          :outline-coordinates="report.coordinates || []"
          :circle="circleOutline"
          height="460px"
        />
      </div>

      <v-alert v-if="report.status === 'FAILED'" type="error" dense text class="mt-2">
        {{ $t("reports.farmProductivity.failed") }}
      </v-alert>

      <!-- Description -->
      <p class="mt-4 mb-2 grey--text text--darken-2">{{ $t("reports.farmProductivity.reportDescription") }}</p>

      <v-divider class="my-4" />

      <!-- Report details (productivity zones) -->
      <h4 class="mb-3 primary--text">{{ $t("reports.farmProductivity.reportDetails") }}</h4>
      <zone-legend :zones="report.zones || []" />

      <v-alert type="warning" text class="mt-4" border="left" icon="mdi-alert">
        <strong>{{ $t("reports.farmProductivity.disclaimerTitle") }}</strong><br />
        {{ $t("reports.farmProductivity.disclaimer") }}
      </v-alert>

      <v-divider class="my-4" />

      <!-- Feedback -->
      <feedback-display
        :feedback="report.feedback"
        i18n-prefix="reports.farmProductivity"
        @add="onAddFeedback"
      />
    </v-card>

    <feedback-dialog
      :value="showFeedback"
      :report-uid="reportUid"
      :service="feedbackService"
      :default-agree="feedbackDefaultAgree"
      i18n-prefix="reports.farmProductivity"
      upload-folder="farm-productivity/feedback"
      @input="showFeedback = $event"
      @submitted="onFeedbackSubmitted"
    />
    <guide-dialog :value="showGuide" @input="showGuide = $event" />

    <confirm-box
      v-if="deleteDialog"
      :dialog="deleteDialog"
      :title="$t('reports.farmProductivity.deleteConfirmTitle')"
      :message="$t('reports.farmProductivity.deleteConfirmMessage')"
      :confirm-btn-text="$t('reports.farmProductivity.delete')"
      :cancel-btn-text="$t('reports.farmProductivity.cancel')"
      @continue:action="doDelete"
      @cancel:action="deleteDialog = false"
    />
  </div>
</template>

<script>
import FarmProductivityService from "@/_services/FarmProductivityService";
// Reuse the read-only raster-overlay map from Crop Progression.
import IndexOverlayMap from "../crop-progression/components/IndexOverlayMap.vue";
import ZoneLegend from "./components/ZoneLegend.vue";
import FeedbackDialog from "../shared/FeedbackDialog.vue";
import FeedbackDisplay from "../shared/FeedbackDisplay.vue";
import GuideDialog from "./components/GuideDialog.vue";
import ConfirmBox from "@/components/ConfirmBox.vue";
import loadingMixin from "@/mixins/LoadingMixin";
import downloadMixin from "@/mixins/DownloadMixin";

const POLL_MS = 4000;

export default {
  name: "FarmProductivityView",
  components: { IndexOverlayMap, ZoneLegend, FeedbackDialog, FeedbackDisplay, GuideDialog, ConfirmBox },
  mixins: [loadingMixin, downloadMixin],
  data() {
    return {
      report: null,
      showFeedback: false,
      feedbackDefaultAgree: null,
      showGuide: false,
      deleteDialog: false,
      downloading: false,
      pollTimer: null,
    };
  },
  computed: {
    feedbackService() {
      return FarmProductivityService;
    },
    reportUid() {
      return this.$route.params.reportUid;
    },
    isGenerating() {
      return this.report && this.report.status === "GENERATING";
    },
    isDownloadable() {
      return this.report && this.report.status === "COMPLETED";
    },
    overlays() {
      if (this.report && this.report.imageUrl && this.report.bbox) {
        return [{ url: this.report.imageUrl, bbox: this.report.bbox, opacity: 1, clipRightPct: null }];
      }
      return [];
    },
    circleOutline() {
      if (this.report && this.report.radius) {
        return {
          lat: this.report.centerLatitude,
          lng: this.report.centerLongitude,
          radiusKm: this.report.radius,
        };
      }
      return null;
    },
    coordLabel() {
      if (!this.report || this.report.centerLatitude == null) return "";
      return `${Number(this.report.centerLatitude).toFixed(6)}, ${Number(this.report.centerLongitude).toFixed(6)}`;
    },
  },
  watch: {
    "$route.params.reportUid"() {
      this.reload();
    },
  },
  created() {
    this.reload();
  },
  beforeDestroy() {
    this.clearPoll();
  },
  methods: {
    clearPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async reload() {
      this.clearPoll();
      this.startLoading();
      try {
        await this.fetchReport();
        if (this.isGenerating) this.startPolling();
      } finally {
        this.stopLoading();
      }
    },
    async fetchReport() {
      try {
        const env = await FarmProductivityService.getReport(this.reportUid);
        this.report = (env && env.data) || null;
      } catch (e) {
        this.$notify({ text: this.$t("reports.farmProductivity.loadFailed"), type: "error" });
      }
    },
    startPolling() {
      this.clearPoll();
      this.pollTimer = setInterval(async () => {
        await this.fetchReport();
        if (!this.isGenerating) this.clearPoll();
      }, POLL_MS);
    },
    async download() {
      this.downloading = true;
      try {
        const res = await FarmProductivityService.downloadPdf(this.reportUid);
        this.downloadBlob(res.data, `farm_productivity_${this.reportUid}.pdf`);
      } catch (e) {
        this.$notify({ text: this.$t("reports.farmProductivity.loadFailed"), type: "error" });
      } finally {
        this.downloading = false;
      }
    },
    onAddFeedback(agree) {
      this.feedbackDefaultAgree = agree;
      this.showFeedback = true;
    },
    onFeedbackSubmitted() {
      this.showFeedback = false;
      this.$notify({ title: this.$t("success"), text: this.$t("reports.farmProductivity.feedbackSubmitted"), type: "success" });
      this.fetchReport();
    },
    async doDelete() {
      this.deleteDialog = false;
      this.startLoading();
      try {
        await FarmProductivityService.deleteReport(this.reportUid);
        this.$notify({ title: this.$t("success"), text: this.$t("reports.farmProductivity.deleted"), type: "success" });
        this.$router.push({ name: "farmProductivityList" });
      } catch (e) {
        this.$notify({ text: this.$t("reports.farmProductivity.loadFailed"), type: "error" });
      } finally {
        this.stopLoading();
      }
    },
    goBack() {
      this.$router.push({ name: "farmProductivityList" });
    },
  },
};
</script>

<style scoped>
.map-holder {
  position: relative;
}
.gen-overlay {
  position: absolute;
  z-index: 5;
  inset: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}
.how-to {
  border: 1px solid #ffe1a6;
}
.how-to a {
  cursor: pointer;
}
</style>
